import { createFeatureSelector, createSelector } from '@ngrx/store';
import { PlanState } from './plan.reducer';
import * as fromPlan from './plan.reducer';
import { PlanQuery } from './plan.actions';
import { LoadingState, getError } from 'app/utils/callstate';
import * as _ from 'lodash';
import { PlanEntry } from 'app/api/plan.api.service';
import { AnalyticsSelectors } from 'app/main/analytics/selectors';
import { selectBalanceSorted } from '../balance/balance.selectors';
import { AnalyticsEntry } from 'app/api/analytics.api.service';

export const selectPlanState = createFeatureSelector<PlanState>('plan');

export const selectPlan = createSelector(
  selectPlanState,
  fromPlan.selectAll
);

export const selectPlanSorted = createSelector(
  selectPlan,
  plan => _.sortBy(plan.map(p => {
    return {
      ...p,
      rac1: p.account.slice(0, 1),
      rac2: p.account.slice(0, 2),
      rac3: p.account.slice(0, 3),
    };
  }), 'account')
);

// Required by Plan Component
export const selectPlanAnalytics = createSelector(
  selectPlanSorted,
  AnalyticsSelectors.selectFlatAna3,
  selectBalanceSorted,
  (plan, ana3, balance) => {
    return plan.map(p => {
      const ana3Entry = ana3.find(el => el.ana3Key === p.anaKey);
      const found = !_.isEmpty(ana3Entry);
      const { entryLabel } = balance.find(el => el.account === p.account) || { entryLabel: ''};
      return {
        ...p,
        ana1KeyValue: found ? `${ana3Entry.ana1Key}|${ana3Entry.ana1Name}` : '',
        ana2KeyValue: found ? `${ana3Entry.ana2Key}|${ana3Entry.ana2Name}` : '',
        ana3KeyValue: found ? `${ana3Entry.ana3Key}|${ana3Entry.ana3Name}` : '',
        accountLabel: entryLabel
      };
    });
  }
);

// Necessary to dynamically map the cube
export const selectFlatPlanAnalytics = createSelector(
  selectPlanSorted,
  AnalyticsSelectors.selectFlatAna3,
  (plan, ana3) => {
    return _.sortBy(plan.map(p => {
      const ana3Entry = ana3.find(el => el.ana3Key === p.anaKey);
      const found = !_.isEmpty(ana3Entry);
      return {
        account: p.account,
        rac1: p.rac1,
        rac2: p.rac2,
        rac3: p.rac3,
        ana1Key: found ? ana3Entry.ana1Key : '',
        ana2Key: found ? ana3Entry.ana2Key : '',
        ana3Key: found ? ana3Entry.ana3Key : '',
        ana1Name: found ? ana3Entry.ana1Name : '',
        ana2Name: found ? ana3Entry.ana2Name : '',
        ana3Name: found ? ana3Entry.ana3Name : '',
        ana1SortOrder: found ? ana3Entry.ana1SortOrder : 0,
        ana2SortOrder: found ? ana3Entry.ana2SortOrder : 0,
        ana3SortOrder: found ? ana3Entry.ana3SortOrder : 0,
        paymentPeriod: found ? ana3Entry.paymentPeriod : '',
      };
    }), ['ana1SortOrder', 'ana2SortOrder', 'ana3SortOrder']);
  }
);

export const selectAna2ForRac1 = rac1 => createSelector(
  AnalyticsSelectors.selectAna2,
  selectFlatPlanAnalytics,
  (ana2, plan) => {
    const racPlan = plan.filter(p => p.rac1 === rac1);
    const ana2Keys = _.groupBy(racPlan, rp => rp.ana2Key);
    return ana2.filter(a => ana2Keys[a.key]);
  }
);
export const selectAna2ForAccount6 = createSelector(
  selectAna2ForRac1('6'),
  ana2 => ana2
);
export const selectAna2ForAccount7 = createSelector(
  selectAna2ForRac1('7'),
  ana2 => ana2
);

export const selectAna3WithAna1Ana2Count = createSelector(
  AnalyticsSelectors.selectAna1,
  AnalyticsSelectors.selectAna2,
  AnalyticsSelectors.selectAna3,
  selectPlanSorted,
  (ana1, ana2, ana3, plan) => {
    return ana3.map((ana3Entry: AnalyticsEntry) => {
      const ana2Entry = ana2.find(el => el.key === ana3Entry.parentKey);
      const ana1Entry = ana1.find(el => el.key === ana2Entry.parentKey);
      const countAccounts = _.countBy(plan, { anaKey: ana3Entry.key });
      return { 
        ...ana3Entry,
        ana1Name: ana1Entry.name,
        ana2KeyValue: `${ana2Entry.key}|${ana2Entry.name}`,
        countAccounts: countAccounts.true
      };
    });
  }
);

export const selectPlanLoading = createSelector(
  selectPlanState,
  planState => planState.callState === LoadingState.Loading
);

export const selectPlanLoaded = createSelector(
  selectPlanState,
  planState => planState.callState === LoadingState.Loaded
);

export const selectPlanError = createSelector(
  selectPlanState,
  planState => getError(planState.callState)
);

export const selectPlanQuery = createSelector(
  selectPlanState,
  planState => ({
    companyId: planState.companyId,
    account: planState.account,
    filter: planState.filter
  } as PlanQuery)
);

export const selectPlanCountAnomalies = createSelector(
  selectPlanSorted,
  entries => _.reduce(entries, (count: number, entry: PlanEntry) => {
      if (_.isEmpty(entry.anaKey)) { count += 1; }
      return count;
    }, 0)
);
